import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPen, FaRegTrashAlt } from 'react-icons/fa';
import { MdExpandMore } from 'react-icons/md';
import dayjs, { Dayjs } from 'dayjs';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { getColors } from '../../../../styles/color-tokens';

import {
  InterestRateType,
  LoanPayload,
  LoanReadModel,
  PermissionKeys,
  RepaymentType,
  useDeleteSpacesByIdLoansAndLoanIdMutation,
  useGetSpacesByIdLoansQuery,
  usePostSpacesByIdLoansMutation,
  usePutSpacesByIdLoansAndLoanIdMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  DatepickerV2,
  EmptyPlaceholder,
  Input,
  Modal,
  ModalFooter,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  toCurrencyString,
} from '@client/toolkit';
import type { CurrencyCode } from '@client/toolkit';

import { DeleteConfirmationModalContent } from '../../../../components/delete-confirmation-modal-content/delete-confirmation-modal-content';
import { useSpacePermissions } from '../../../../hooks/useSpacePermissions/useSpacePermissions';

export interface LoanCardProps {
  buildingId?: string;
}

interface LoanEditState {
  loanId: string | null; // null = new
  lender: string;
  originalAmount: string;
  outstandingAmount: string;
  interestRate: string;
  interestRateType: InterestRateType;
  repaymentType: RepaymentType;
  startDate: Dayjs | null;
  maturityDate: Dayjs | null;
  annualPayment: string;
  covenantNotes: string;
}

const emptyEdit = (): LoanEditState => ({
  loanId: null,
  lender: '',
  originalAmount: '',
  outstandingAmount: '',
  interestRate: '',
  interestRateType: 'Floating',
  repaymentType: 'Annuity',
  startDate: null,
  maturityDate: null,
  annualPayment: '',
  covenantNotes: '',
});

const fromLoan = (loan: LoanReadModel): LoanEditState => ({
  loanId: loan.id,
  lender: loan.lender ?? '',
  originalAmount: loan.originalAmount?.toString() ?? '',
  outstandingAmount: loan.outstandingAmount?.toString() ?? '',
  interestRate: loan.interestRate?.toString() ?? '',
  interestRateType: loan.interestRateType,
  repaymentType: loan.repaymentType,
  startDate: loan.startDate ? dayjs(loan.startDate) : null,
  maturityDate: loan.maturityDate ? dayjs(loan.maturityDate) : null,
  annualPayment: loan.annualPayment?.toString() ?? '',
  covenantNotes: loan.covenantNotes ?? '',
});

export function LoanCard({ buildingId }: LoanCardProps) {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation('portfolio');

  const { data: loans, isLoading } = useGetSpacesByIdLoansQuery({ id: buildingId ?? '' }, { skip: !buildingId });

  const [createLoan, { isLoading: isCreating }] = usePostSpacesByIdLoansMutation();
  const [updateLoan, { isLoading: isUpdating }] = usePutSpacesByIdLoansAndLoanIdMutation();
  const [deleteLoan, { isLoading: isDeleting }] = useDeleteSpacesByIdLoansAndLoanIdMutation();
  const { hasAnyPermission } = useSpacePermissions({ id: buildingId });
  const canEdit = hasAnyPermission([PermissionKeys.Space.Update]);

  const isSaving = isCreating || isUpdating;

  const [editing, setEditing] = useState(false);
  const [editRow, setEditRow] = useState<LoanEditState | null>(null);
  const [deletingLoan, setDeletingLoan] = useState<LoanReadModel | null>(null);

  const handleOpenEdit = () => setEditing(true);

  const handleAdd = () => setEditRow(emptyEdit());

  const handleEditRow = (loan: LoanReadModel) => setEditRow(fromLoan(loan));

  const handleCancel = () => {
    setEditRow(null);
    setEditing(false);
  };

  const handleCancelRow = () => setEditRow(null);

  const handleSave = async () => {
    if (!buildingId || !editRow) return;

    const payload: LoanPayload = {
      lender: editRow.lender || null,
      originalAmount: editRow.originalAmount ? parseFloat(editRow.originalAmount) : null,
      outstandingAmount: editRow.outstandingAmount ? parseFloat(editRow.outstandingAmount) : null,
      interestRate: editRow.interestRate ? parseFloat(editRow.interestRate) : null,
      interestRateType: editRow.interestRateType,
      repaymentType: editRow.repaymentType,
      startDate: editRow.startDate?.format('YYYY-MM-DD') ?? null,
      maturityDate: editRow.maturityDate?.format('YYYY-MM-DD') ?? null,
      annualPayment: editRow.annualPayment ? parseFloat(editRow.annualPayment) : null,
      covenantNotes: editRow.covenantNotes || null,
    };

    try {
      if (editRow.loanId) {
        await updateLoan({ id: buildingId, loanId: editRow.loanId, body: payload }).unwrap();
      } else {
        await createLoan({ id: buildingId, body: payload }).unwrap();
      }
      setEditRow(null);
    } catch {
      // error handled by RTK
    }
  };

  const handleDeleteRequest = (loan: LoanReadModel) => setDeletingLoan(loan);

  const handleDeleteConfirm = async () => {
    if (!buildingId || !deletingLoan) return;
    try {
      await deleteLoan({ id: buildingId, loanId: deletingLoan.id }).unwrap();
    } catch {
      // error handled by RTK
    }
    setDeletingLoan(null);
  };

  const handleDeleteCancel = () => setDeletingLoan(null);

  const formatAmount = (val?: number | null, currency = 'NOK') =>
    toCurrencyString(val ?? undefined, '\u2013', { currency: currency as CurrencyCode });

  const formatDate = (val?: string | null) => {
    if (!val) return '\u2013';
    const parts = val.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return val;
  };

  const isEditingRow = (loan: LoanReadModel) => editRow !== null && editRow.loanId === loan.id;

  const isListEmpty = !loans?.length && !editing;

  // ── Inline edit rows definition ──
  const editFields: {
    label: string;
    field: keyof LoanEditState;
    type: 'text' | 'number' | 'datepicker' | 'select' | 'radio';
  }[] = [
    { label: t('loan.fields.lender'), field: 'lender', type: 'text' },
    { label: t('loan.fields.originalAmount'), field: 'originalAmount', type: 'number' },
    { label: t('loan.fields.outstandingAmount'), field: 'outstandingAmount', type: 'number' },
    { label: t('loan.fields.interestRate'), field: 'interestRate', type: 'number' },
    { label: t('loan.fields.interestRateType'), field: 'interestRateType', type: 'radio' },
    { label: t('loan.fields.repaymentType'), field: 'repaymentType', type: 'select' },
    { label: t('loan.fields.startDate'), field: 'startDate', type: 'datepicker' },
    { label: t('loan.fields.maturityDate'), field: 'maturityDate', type: 'datepicker' },
    { label: t('loan.fields.annualPayment'), field: 'annualPayment', type: 'number' },
    { label: t('loan.fields.covenantNotes'), field: 'covenantNotes', type: 'text' },
  ];

  // ── Read-mode rows per loan ──
  const readFields = (loan: LoanReadModel): { label: string; display: string }[] => [
    { label: t('loan.fields.lender'), display: loan.lender || '\u2013' },
    { label: t('loan.fields.originalAmount'), display: formatAmount(loan.originalAmount, loan.currency) },
    { label: t('loan.fields.outstandingAmount'), display: formatAmount(loan.outstandingAmount, loan.currency) },
    { label: t('loan.fields.interestRate'), display: loan.interestRate != null ? `${loan.interestRate} %` : '\u2013' },
    {
      label: t('loan.fields.interestRateType'),
      display: loan.interestRateType ? t(`loan.interestRateType.${loan.interestRateType}`) : '\u2013',
    },
    {
      label: t('loan.fields.repaymentType'),
      display: loan.repaymentType ? t(`loan.repaymentType.${loan.repaymentType}`) : '\u2013',
    },
    { label: t('loan.fields.startDate'), display: formatDate(loan.startDate) },
    { label: t('loan.fields.maturityDate'), display: formatDate(loan.maturityDate) },
    { label: t('loan.fields.annualPayment'), display: formatAmount(loan.annualPayment, loan.currency) },
    { label: t('loan.fields.covenantNotes'), display: loan.covenantNotes || '\u2013' },
  ];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex-1" variant="highlighted">
            {t('loan.title')}
          </CardTitle>
          <CardActions>
            {!editing ? (
              canEdit && (
                <Button size="sm" onClick={handleOpenEdit}>
                  {tCommon('edit')}
                </Button>
              )
            ) : (
              <>
                <Button size="sm" color="primary-dark" onClick={handleCancel} disabled={isSaving}>
                  {tCommon('cancel')}
                </Button>
                <Button size="sm" color="primary-dark" onClick={handleAdd} disabled={isSaving || editRow !== null}>
                  {tCommon('add')}
                </Button>
                {editRow !== null && (
                  <Button size="sm" color="primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? t('loan.saving') : tCommon('save')}
                  </Button>
                )}
              </>
            )}
          </CardActions>
        </CardHeader>

        <CardContent variant="fill" className="justify-self-center rounded-lg w-full px-4 pb-4">
          {isLoading &&
            Array.from(Array(2).keys()).map((x) => (
              <div key={x} className="h-12 mb-2 rounded bg-surface-elevated animate-pulse" />
            ))}

          {!isLoading && isListEmpty && (
            <div className="px-4 py-4">
              <EmptyPlaceholder>{tCommon('noEntityFound', { entity: t('loan.entityName') })}</EmptyPlaceholder>
            </div>
          )}

          {/* Existing loans as accordions */}
          {!isLoading &&
            loans?.map((loan) => (
              <LoanAccordion
                key={loan.id}
                loan={loan}
                editing={editing}
                isEditing={isEditingRow(loan)}
                editRow={editRow}
                setEditRow={setEditRow}
                editFields={editFields}
                readFields={readFields(loan)}
                formatAmount={formatAmount}
                onEdit={() => handleEditRow(loan)}
                onDelete={() => handleDeleteRequest(loan)}
                onCancelRow={handleCancelRow}
              />
            ))}

          {/* New loan inline form */}
          {editing && editRow !== null && editRow.loanId === null && (
            <NewLoanForm editRow={editRow} setEditRow={setEditRow} editFields={editFields} />
          )}

          {/* Total outstanding */}
          {!isLoading && loans && loans.length > 0 && (
            <div className="flex items-center justify-between px-3 py-3 mt-2 border-t border-border">
              <strong className="text-sm text-white">{t('loan.totalOutstanding')}</strong>
              <strong className="text-sm text-white">
                {formatAmount(
                  loans.reduce((sum, l) => sum + (l.outstandingAmount ?? 0), 0),
                  loans[0]?.currency ?? 'NOK',
                )}
              </strong>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation modal */}
      <Modal
        open={deletingLoan !== null}
        onClose={handleDeleteCancel}
        onBackdropClick={handleDeleteCancel}
        closeDisabled={isDeleting}
      >
        <DeleteConfirmationModalContent name={deletingLoan?.lender ?? t('loan.entityName')} />
        <ModalFooter className="justify-center">
          <Button color="danger" onClick={handleDeleteConfirm} progressing={isDeleting}>
            {tCommon('delete')}
          </Button>
          <Button color="primary-dark" onClick={handleDeleteCancel}>
            {tCommon('cancel')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Loan accordion (read or edit for a single loan)
// ---------------------------------------------------------------------------

interface LoanAccordionProps {
  loan: LoanReadModel;
  editing: boolean;
  isEditing: boolean;
  editRow: LoanEditState | null;
  setEditRow: (state: LoanEditState) => void;
  editFields: {
    label: string;
    field: keyof LoanEditState;
    type: 'text' | 'number' | 'datepicker' | 'select' | 'radio';
  }[];
  readFields: { label: string; display: string }[];
  formatAmount: (val?: number | null, currency?: string) => string;
  onEdit: () => void;
  onDelete: () => void;
  onCancelRow: () => void;
}

function LoanAccordion({
  loan,
  editing,
  isEditing,
  editRow,
  setEditRow,
  editFields,
  readFields,
  formatAmount,
  onEdit,
  onDelete,
  onCancelRow,
}: LoanAccordionProps) {
  const { t } = useTranslation('portfolio');
  const { t: tCommon } = useTranslation();
  const colors = getColors();
  const [expanded, setExpanded] = useState(false);

  const handleEdit = () => {
    setExpanded(true);
    onEdit();
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      sx={{
        backgroundColor: colors.background.surface.base,
        color: colors.text.primary,
        boxShadow: 'none',
        '&:before': { display: 'none' },
        border: `1px solid ${colors.border.default}`,
        borderRadius: '8px !important',
        marginBottom: '8px',
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<MdExpandMore className="text-white" />}
        sx={{
          minHeight: 40,
          '& .MuiAccordionSummary-content': { margin: '8px 0', alignItems: 'center' },
        }}
      >
        <span className="flex-1 font-semibold text-sm">{loan.lender || t('loan.noLender')}</span>
        <span className="text-sm text-gray-400 mr-4">{formatAmount(loan.outstandingAmount, loan.currency)}</span>
        {editing && !isEditing && (
          <div className="flex items-center gap-1 mr-2" onClick={(e) => e.stopPropagation()}>
            <Button className="text-brand-primary bg-surface-elevated" size="xs" color="custom" onClick={handleEdit}>
              <FaPen />
            </Button>
            <Button className="text-brand-primary bg-surface-elevated" size="xs" color="custom" onClick={onDelete}>
              <FaRegTrashAlt />
            </Button>
          </div>
        )}
        {editing && isEditing && (
          <div className="mr-2" onClick={(e) => e.stopPropagation()}>
            <Button className="text-gray-400" size="xs" color="custom" onClick={onCancelRow}>
              {tCommon('cancel')}
            </Button>
          </div>
        )}
      </AccordionSummary>

      <AccordionDetails sx={{ padding: 0 }}>
        <Table className="overflow-hidden w-full rounded-md" size="xs" layout="fixed">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{t('loan.fields.type')}</TableHeadCell>
              <TableHeadCell align="right">{t('loan.fields.value')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isEditing && editRow
              ? editFields.map((ef) => (
                  <InlineEditFieldRow
                    key={ef.field}
                    label={ef.label}
                    field={ef.field}
                    type={ef.type}
                    editRow={editRow}
                    setEditRow={setEditRow}
                  />
                ))
              : readFields.map((rf) => (
                  <TableRow key={rf.label} verticalAlign="middle">
                    <TableCell>
                      <strong>{rf.label}</strong>
                    </TableCell>
                    <TableCell align="right">{rf.display}</TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
}

// ---------------------------------------------------------------------------
// New loan form (shown when adding, not inside an accordion)
// ---------------------------------------------------------------------------

interface NewLoanFormProps {
  editRow: LoanEditState;
  setEditRow: (state: LoanEditState) => void;
  editFields: {
    label: string;
    field: keyof LoanEditState;
    type: 'text' | 'number' | 'datepicker' | 'select' | 'radio';
  }[];
}

function NewLoanForm({ editRow, setEditRow, editFields }: NewLoanFormProps) {
  const { t } = useTranslation('portfolio');
  return (
    <div className="mt-2">
      <div className="px-2 py-2">
        <strong className="text-brand-primary text-xs uppercase">{t('loan.newLoan')}</strong>
      </div>
      <Table className="overflow-hidden w-full rounded-md" size="xs" layout="fixed">
        <TableHead>
          <TableHeadRow>
            <TableHeadCell>{t('loan.fields.type')}</TableHeadCell>
            <TableHeadCell align="right">{t('loan.fields.value')}</TableHeadCell>
          </TableHeadRow>
        </TableHead>
        <TableBody>
          {editFields.map((ef) => (
            <InlineEditFieldRow
              key={ef.field}
              label={ef.label}
              field={ef.field}
              type={ef.type}
              editRow={editRow}
              setEditRow={setEditRow}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline edit field row
// ---------------------------------------------------------------------------

interface InlineEditFieldRowProps {
  label: string;
  field: keyof LoanEditState;
  type: 'text' | 'number' | 'datepicker' | 'select' | 'radio';
  editRow: LoanEditState;
  setEditRow: (state: LoanEditState) => void;
}

function InlineEditFieldRow({ label, field, type, editRow, setEditRow }: InlineEditFieldRowProps) {
  const { t } = useTranslation('portfolio');
  const value = editRow[field] as string;

  const handleChange = (v: string) => setEditRow({ ...editRow, [field]: v });

  return (
    <TableRow verticalAlign="middle">
      <TableCell>
        <strong>{label}</strong>
      </TableCell>
      <TableCell align="right">
        {type === 'radio' && field === 'interestRateType' && (
          <div className="flex items-center justify-end gap-4">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="interestRateType"
                value="Floating"
                checked={value === 'Floating'}
                onChange={(e) => handleChange(e.target.value)}
                className="accent-brand-primary"
              />
              <span className="text-sm">{t('loan.interestRateType.Floating')}</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="interestRateType"
                value="Fixed"
                checked={value === 'Fixed'}
                onChange={(e) => handleChange(e.target.value)}
                className="accent-brand-primary"
              />
              <span className="text-sm">{t('loan.interestRateType.Fixed')}</span>
            </label>
          </div>
        )}

        {type === 'select' && field === 'repaymentType' && (
          <Select size="medium" value={value} onChange={(e) => handleChange(e.target.value)}>
            <option value="Annuity">{t('loan.repaymentType.Annuity')}</option>
            <option value="Serial">{t('loan.repaymentType.Serial')}</option>
            <option value="InterestOnly">{t('loan.repaymentType.InterestOnly')}</option>
          </Select>
        )}

        {type === 'datepicker' && (field === 'startDate' || field === 'maturityDate') && (
          <DatepickerV2
            value={(editRow[field] as Dayjs | null) ?? undefined}
            onChange={(date) => setEditRow({ ...editRow, [field]: date ?? null })}
            className="w-full"
          />
        )}

        {(type === 'text' || type === 'number') && (
          <Input
            type={type}
            size="medium"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={type === 'number' ? '0' : ''}
            className="dark:bg-surface-base"
          />
        )}
      </TableCell>
    </TableRow>
  );
}
